import type { PlayerState } from "./playerSlice";

import { Tableau } from "../../types";

/** Clé de jour civil pour regrouper les tableaux (même journée de compétition). */
export const calendarDayKey = (date: Date): string => {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Si `false`, le tableau ne compte pas dans la limite de 2 tableaux / jour. Défaut : compte. */
export const boardCountsTowardDailyLimit = (
  board: Pick<Tableau, "comptePourLimiteJournaliere">
): boolean => board.comptePourLimiteJournaliere !== false;

/**
 * Au plus 2 tableaux par jour parmi ceux qui comptent pour la limite (`comptePourLimiteJournaliere`).
 */
export const isDailySelectionValid = (
  boardIds: string[],
  boards: Tableau[]
): boolean => {
  const unique = Array.from(new Set(boardIds)).filter(Boolean);
  const byId = new Map(boards.map((b) => [String(b.id), b]));
  const countingByDay = new Map<string, number>();
  for (const id of unique) {
    const b = byId.get(String(id));
    if (!b || !boardCountsTowardDailyLimit(b)) continue;
    const key = calendarDayKey(b.date);
    countingByDay.set(key, (countingByDay.get(key) ?? 0) + 1);
  }
  for (const n of Array.from(countingByDay.values())) {
    if (n > 2) return false;
  }
  return true;
};

/** Tableaux effectivement retenus : inscriptions existantes + nouvelles sélections − désélections. */
export const getEffectiveSelectedBoardIds = (state: PlayerState): string[] => {
  const player = state.player;
  if (!player) return [];
  const fromParticipation =
    player.participation?.map((p) => p.tableauId) ?? [];
  const merged = Array.from(
    new Set([...fromParticipation, ...state.selectedBoardIds])
  );
  return merged.filter((id) => !state.unselectedBoardIds.includes(id));
};

/** Vérifie si deux tableaux parmi `boardIds` sont déclarés incompatibles (données symétriques). */
export const hasIncompatiblePairAmong = (
  boardIds: string[],
  boards: Tableau[]
): boolean => {
  const ids = Array.from(new Set(boardIds)).filter(Boolean);
  const byId = new Map(boards.map((b) => [String(b.id), b]));
  for (let i = 0; i < ids.length; i++) {
    const inc = byId.get(String(ids[i]))?.incompatibleBoardIds ?? [];
    for (let j = i + 1; j < ids.length; j++) {
      if (inc.includes(ids[j])) return true;
    }
  }
  return false;
};

/**
 * Le joueur ne peut pas ajouter ce tableau : il entre en conflit avec la sélection courante.
 * (Si déjà dans la sélection, on laisse l’action pour pouvoir désélectionner.)
 */
export const isBoardBlockedByIncompatibility = (
  boardId: string,
  state: PlayerState
): boolean => {
  const eff = getEffectiveSelectedBoardIds(state);
  if (eff.includes(boardId)) return false;
  const boards = state.accessibleBoards;
  const b = boards.find((x) => x.id === boardId);
  const incB = b?.incompatibleBoardIds ?? [];
  for (const oid of eff) {
    if (oid === boardId) continue;
    const o = boards.find((x) => x.id === oid);
    if (incB.includes(oid)) return true;
    if (o?.incompatibleBoardIds?.includes(boardId)) return true;
  }
  return false;
};

export const isRegistered = (boardId: string, state: PlayerState) => {
  // Si le tableau est dans unselectedBoardIds, il n'est pas sélectionné
  if (state.unselectedBoardIds.includes(boardId)) {
    return false;
  }

  // Si le tableau est dans selectedBoardIds, il est sélectionné
  if (state.selectedBoardIds.includes(boardId)) {
    return true;
  }

  // Si le tableau est dans les participations du joueur et n'est pas désélectionné
  return (
    state.player?.participation?.some((p) => p.tableauId === boardId) ?? false
  );
};

/**
 * Limite 2 tableaux / jour (tableaux « comptants ») : ajouter ce tableau la dépasserait ?
 */
export const wouldViolateDailyLimitIfAdded = (
  boardId: string,
  state: PlayerState
): boolean => {
  if (isRegistered(boardId, state)) return false;
  const b = state.accessibleBoards.find((x) => String(x.id) === String(boardId));
  if (!b || !boardCountsTowardDailyLimit(b)) return false;
  const keyB = calendarDayKey(b.date);
  const eff = getEffectiveSelectedBoardIds(state);
  let countingOnDay = 0;
  for (const id of eff) {
    const bo = state.accessibleBoards.find((x) => String(x.id) === String(id));
    if (
      bo &&
      calendarDayKey(bo.date) === keyB &&
      boardCountsTowardDailyLimit(bo)
    ) {
      countingOnDay += 1;
    }
  }
  return countingOnDay >= 2;
};

export const isDisabled = (
  boardId: string,
  state: PlayerState,
  isAdmin: boolean,
  allowAdminDailyLimitBypass: boolean
) => {
  // tableau.obligatoire  ne mettre à disabled qu'en cas d'inscription déjà effectuée et déjà inscrit sur un autre tableau
  return (
    // Max 2 tableaux / jour parmi les tableaux qui comptent pour la limite
    ((!isAdmin || !allowAdminDailyLimitBypass) &&
      wouldViolateDailyLimitIfAdded(boardId, state)) ||
    // non admin tableau fermé ou full et pas encore inscrit sur ce tableau -> on ne peut plus s'inscrire
    (!isAdmin &&
      (infosTableau(boardId, state).ferme ||
        infosTableau(boardId, state).isFull) &&
      !isRegistered(boardId, state)) ||
    // non admin, déjà inscrit au tableau auparavant. on ne peut plus supprimer => devient inutile car un désactive tout dès qu'il y a une inscriptio
    (!isAdmin &&
      state.player?.participation?.find((p) => p.tableauId === boardId) !==
        undefined) ||
    // non admin, déjà inscrit à au moins un tableau, on désactive toute modification
    (!isAdmin && state.player!.participation!.length > 0) ||
    // on ne peut pas supprimer un tableau obligatoire si au moins une autre inscription
    (infosTableau(boardId, state).obligatoire &&
      isRegistered(boardId, state) &&
      getEffectiveSelectedBoardIds(state).length >= 2) ||
    isBoardBlockedByIncompatibility(boardId, state)
  );
};

const infosTableau = (boardId: string, state: PlayerState): Tableau => {
  return state.accessibleBoards.find((ab) => ab.id === boardId)!;
};
